import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { api } from "@/redux/api";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import ContractList from "@/containers/contract/ContractList";
import { useAuthEditable } from "@/hooks/use-auth";

export default function FacilityContractPage() {
  const selectedFacilityId = useSelector((state) => state.Facility?.selected?.Id);
  const selectedFacility = useSelector((state) => state.Facility?.selected);
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const { companyId: routeCompanyId, facilityId: routeFacilityId } = useParams();
  const canCreate = useAuthEditable("contract");
  const companyId = routeCompanyId || facilityContext.companyId;
  const facilityIds = useMemo(() => {
    const currentFacilityId = Number(routeFacilityId) || selectedFacilityId;
    return currentFacilityId ? [currentFacilityId] : [];
  }, [routeFacilityId, selectedFacilityId]);
  const currentFacilityId = facilityIds[0];

  // Contractor directory powering the "Awarded Contractor" filter. Scoped to the
  // facility on screen so the options match the contractors that can actually be
  // awarded on its contract requests.
  const [awardedContractorIds, setAwardedContractorIds] = useState([]);
  const [contractorOptions, setContractorOptions] = useState([]);

  useEffect(() => {
    if (!currentFacilityId) {
      setContractorOptions([]);
      return undefined;
    }
    let active = true;
    api.getContractors(currentFacilityId)
      .then((response) => {
        if (!active) return;
        setContractorOptions(
          (response.data || []).map((company) => ({
            id: company.id,
            name: company.name,
            email: company.email,
            logo: company.logo,
          }))
        );
      })
      .catch(() => {
        if (active) setContractorOptions([]);
      });
    return () => {
      active = false;
    };
  }, [currentFacilityId]);

  // Drop any stale selection when the facility changes, otherwise the filter
  // would keep hiding every row behind an id that no longer applies.
  useEffect(() => {
    setAwardedContractorIds([]);
  }, [currentFacilityId]);

  const loadRequests = useCallback(
    (selectedFacilityIds, params) => api.getContracts(selectedFacilityIds, params),
    []
  );
  const getDetailPath = useCallback(
    (record) => {
      return buildFacilityPath(`contract-request-detail/${record.id}`, facilityContext) || "/switch-view";
    },
    [facilityContext]
  );
  const getWorkorderPath = useCallback(
    (record) => {
      const recordContext = {
        companyId: record?.company_id || facilityContext.companyId,
        facilityId:
          record?.workorder_facility_id || record?.facility || facilityContext.facilityId,
      };
      return (
        buildFacilityPath(`workorder-detail/${record.workorder_id}`, recordContext) ||
        "/switch-view"
      );
    },
    [facilityContext]
  );

  return (
    <ContractList
      facilityIds={facilityIds}
      facilities={facilityIds.map((id) => ({
        id,
        name: selectedFacility?.name || selectedFacility?.Name || `Facility #${id}`,
      }))}
      companyIds={companyId ? [companyId] : []}
      companies={companyId ? [{
        id: companyId,
        name: selectedFacility?.company_name || selectedFacility?.company?.name || `Company #${companyId}`,
      }] : []}
      contractors={contractorOptions}
      awardedContractorIds={awardedContractorIds}
      onAwardedContractorChange={setAwardedContractorIds}
      loadRequests={loadRequests}
      getDetailPath={getDetailPath}
      getWorkorderPath={getWorkorderPath}
      showRequesterColumn
      canCreate={canCreate}
      enableContractorModal
      viewerRole="facility"
      createPath={buildFacilityPath("create-contract-request", facilityContext) || "/switch-view"}
    />
  );
}
